import { Badge, Button } from "@design";
import { CheckCircle2, ExternalLink, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import type { ActionProposal } from "./actionModel";

/**
 * Where a decided action stands, in place of the card's "Approval needed" (MHS-971): approved (done, once it has run,
 * or that it didn't work), or declined. Null while it's pending or replaced.
 */
export function ActionStatusBadge({ action }: Readonly<{ action: ActionProposal }>) {
  const { t } = useTranslation("chat", { keyPrefix: "action" });
  if (action.status === "confirmed") {
    if (action.receipt && !action.receipt.ok)
      return (
        <Badge variant="danger" icon={<XCircle size={12} />}>
          {t("receipt.failed")}
        </Badge>
      );
    return (
      <Badge variant="success" icon={<CheckCircle2 size={12} />}>
        {action.receipt ? t("receipt.done") : t("confirmed")}
      </Badge>
    );
  }
  if (action.status === "declined")
    return (
      <Badge variant="neutral" icon={<XCircle size={12} />}>
        {t("declined")}
      </Badge>
    );
  return null;
}

/**
 * What follows a card that can't be acted on here: a link to what the action made, that it was approved for the rest of
 * the chat, or why it can't be decided (someone else's chat, or replaced by a later message).
 */
export function ActionOutcome({ action }: Readonly<{ action: ActionProposal }>) {
  const { t } = useTranslation("chat", { keyPrefix: "action" });
  if (action.status === "confirmed") {
    const url = action.receipt?.url;
    if (!url && !action.remembered) return null;
    return (
      <div className="flex flex-col items-start gap-1">
        {url && (
          <Button
            variant="link"
            size="sm"
            trailingIcon={<ExternalLink size={12} aria-hidden="true" />}
            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          >
            {t("receipt.open")}
          </Button>
        )}
        {action.remembered && (
          <Text variant="caption" tone="tertiary">
            {t("rememberedNote")}
          </Text>
        )}
      </div>
    );
  }
  if (action.status === "declined") return null;
  return (
    <Text variant="caption" tone="tertiary">
      {action.status === "pending" ? t("readOnly") : t("superseded")}
    </Text>
  );
}
